import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type { ScoreType } from '../store/useSubmarketStore';

/**
 * Individual submarket score record from the API.
 */
export interface SubmarketScore {
  submarket_id: number;
  name: string;
  composite_score: number;
  rent_gap_score: number;
  mobi_score: number;
  momentum_score: number;
  vacancy_score: number;
  valuation_score: number;
  permit_score: number;
  spillover_score: number;
  rank: number;
  raw_data?: {
    submarket_egi?: number;
    metro_egi?: number;
    cagr_5yr?: number;
    vacancy_rate?: number;
    permits_per_1000?: number;
  };
}

/**
 * Response from GET /api/submarkets/scores endpoint.
 */
export interface SubmarketScoresResponse {
  submarkets: SubmarketScore[];
  total: number;
  metro_egi: number;
  metro_cagr: number;
  computed_at: string;
}

/**
 * Detailed submarket data from GET /api/submarkets/{id} endpoint.
 */
export interface SubmarketDetailResponse {
  submarket_id: number;
  name: string;
  property_type: string;
  state: string;
  scores: {
    composite: number;
    rent_gap: number;
    mobi: number;
    momentum: number;
    vacancy: number;
    valuation: number;
    permit: number;
    spillover: number;
  };
  rank: number;
  metrics: {
    current_egi: number;
    metro_egi: number;
    cagr_5yr: number;
    vacancy_rate: number;
    permits_per_1000: number;
    cap_rate: number;
    price_per_unit: number;
    rent_growth_1yr: number;
  };
  historical: {
    egi_trend: Array<{ date: string; value: number }>;
    vacancy_trend: Array<{ date: string; value: number }>;
  };
  neighborhoods: Array<{
    id: number;
    name: string;
    egi: number;
    cagr: number;
  }>;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Hook to fetch all submarket scores.
 *
 * Used by the submarket map to color-code polygons and display rankings.
 */
export function useSubmarketScores() {
  return useQuery<SubmarketScoresResponse>({
    queryKey: ['submarkets', 'scores'],
    queryFn: async () => {
      const { data } = await api.get<SubmarketScoresResponse>('/submarkets/scores');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch detailed data for a specific submarket.
 *
 * @param submarketId - The ID of the submarket to fetch
 */
export function useSubmarketDetail(submarketId: number | null) {
  return useQuery<SubmarketDetailResponse>({
    queryKey: ['submarkets', 'detail', submarketId],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/submarkets/${submarketId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch submarket: ${response.status}`);
      }
      return response.json();
    },
    enabled: submarketId !== null,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Get the score value for a specific score type from a SubmarketScore.
 */
export function getScoreValue(score: SubmarketScore, scoreType: ScoreType): number {
  switch (scoreType) {
    case 'composite':
      return score.composite_score;
    case 'rent_gap':
      return score.rent_gap_score;
    case 'mobi':
      return score.mobi_score;
    case 'momentum':
      return score.momentum_score;
    case 'vacancy':
      return score.vacancy_score;
    case 'valuation':
      return score.valuation_score;
    case 'permit':
      return score.permit_score;
    case 'spillover':
      return score.spillover_score;
    default:
      return score.composite_score;
  }
}

/**
 * Get the display color for a score value.
 *
 * Color scale:
 * - 80+: #10b981 (emerald) - Excellent
 * - 65-80: #84cc16 (lime) - Good
 * - 50-65: #eab308 (yellow) - Average
 * - 40-50: #f97316 (orange) - Below Average
 * - <40: #ef4444 (crimson) - Poor
 */
export function getScoreColor(score: number): string {
  if (score >= 80) return '#10b981'; // emerald
  if (score >= 65) return '#84cc16'; // lime
  if (score >= 50) return '#eab308'; // yellow
  if (score >= 40) return '#f97316'; // orange
  return '#ef4444'; // crimson
}

/**
 * Get the human-readable label for a score type.
 */
export function getScoreLabel(scoreType: ScoreType): string {
  switch (scoreType) {
    case 'composite':
      return 'Composite Score';
    case 'rent_gap':
      return 'Rent Gap';
    case 'mobi':
      return 'MOBI Score';
    case 'momentum':
      return 'Momentum';
    case 'vacancy':
      return 'Vacancy';
    case 'valuation':
      return 'Valuation';
    case 'permit':
      return 'Permit Activity';
    case 'spillover':
      return 'Spillover';
    default:
      return 'Score';
  }
}

/**
 * Get the tier classification for a score value.
 *
 * Returns tier name and description for UI display.
 */
export function getScoreTier(score: number): { tier: string; description: string } {
  if (score >= 80) {
    return { tier: 'Excellent', description: 'Top performing submarket' };
  }
  if (score >= 65) {
    return { tier: 'Good', description: 'Above average opportunity' };
  }
  if (score >= 50) {
    return { tier: 'Average', description: 'Market-rate performance' };
  }
  if (score >= 40) {
    return { tier: 'Below Average', description: 'Underperforming area' };
  }
  return { tier: 'Poor', description: 'High risk submarket' };
}

export default useSubmarketScores;
